import {
  computed,
  isRef,
  onScopeDispose,
  shallowRef,
  toValue,
  watch,
  type ComputedRef,
  type InjectionKey,
  type MaybeRefOrGetter,
} from "vue";
import {
  useRoute,
  type RouteLocationNormalizedLoaded,
  type RouteLocationRaw,
} from "vue-router";
import type { BreadcrumbItem } from "@/components/Core/BreadcrumbBar.vue";

export const pageFrameTitleKey: InjectionKey<ComputedRef<string>> =
  Symbol("page-frame-title");
export const pageFrameBreadcrumbsKey: InjectionKey<
  ComputedRef<BreadcrumbItem[]>
> = Symbol("page-frame-breadcrumbs");

export interface PageShellRegistration {
  scope?: string;
  title?: MaybeRefOrGetter<string | undefined>;
  breadcrumbLabel?: MaybeRefOrGetter<string | undefined>;
  breadcrumbTos?: MaybeRefOrGetter<
    Readonly<Record<string, RouteLocationRaw>> | undefined
  >;
  extraBreadcrumbs?: MaybeRefOrGetter<readonly BreadcrumbItem[] | undefined>;
}

interface RegisteredPageShell {
  key: string;
  owner: symbol;
  scope: string;
  title?: string;
  breadcrumbLabel?: string;
  breadcrumbTos: Readonly<Record<string, RouteLocationRaw>>;
  extraBreadcrumbs: readonly BreadcrumbItem[];
}

const registeredPageShell = shallowRef<RegisteredPageShell>();

function routeIdentity(
  route: RouteLocationNormalizedLoaded,
  scope: string,
): string {
  const params = Object.keys(route.params)
    .sort()
    .map((name) => [name, route.params[name]]);
  return `${scope}:${JSON.stringify(params)}`;
}

function routeIncludesScope(
  route: RouteLocationNormalizedLoaded,
  scope: string,
): boolean {
  return route.matched.some(
    (record) => String(record.name ?? record.path) === scope,
  );
}

export function usePageShell(registration: PageShellRegistration): void {
  const route = useRoute();
  const owner = Symbol("page-shell-registration");
  const routeRecord = route.matched.at(-1);
  const scope =
    registration.scope ??
    String(routeRecord?.name ?? routeRecord?.path ?? route.name ?? route.path);
  let previousKey: string | undefined;

  function staticValue<T>(
    source: MaybeRefOrGetter<T> | undefined,
  ): T | undefined {
    return !isRef(source) && typeof source !== "function" ? source : undefined;
  }

  function readRegistration(
    staticOnly = false,
  ): Omit<RegisteredPageShell, "key" | "owner" | "scope"> {
    function read<T>(source: MaybeRefOrGetter<T> | undefined): T | undefined {
      return staticOnly ? staticValue(source) : toValue(source);
    }
    return {
      title: read(registration.title),
      breadcrumbLabel: read(registration.breadcrumbLabel),
      breadcrumbTos: read(registration.breadcrumbTos) ?? {},
      extraBreadcrumbs: read(registration.extraBreadcrumbs) ?? [],
    };
  }

  function publish(
    key: string,
    values: Omit<RegisteredPageShell, "key" | "owner" | "scope">,
  ): void {
    registeredPageShell.value = { ...values, key, owner, scope };
  }

  function clear(): void {
    if (registeredPageShell.value?.owner === owner) {
      registeredPageShell.value = undefined;
    }
  }

  watch(
    [() => routeIdentity(route, scope), () => routeIncludesScope(route, scope)],
    ([key, inScope]) => {
      if (!inScope) {
        clear();
        return;
      }
      const changedRoute = previousKey !== undefined && previousKey !== key;
      previousKey = key;
      publish(key, readRegistration(changedRoute));
    },
    { immediate: true, flush: "sync" },
  );
  watch(
    () => readRegistration(),
    (values) => {
      if (!routeIncludesScope(route, scope)) return;
      publish(routeIdentity(route, scope), values);
    },
    { flush: "sync" },
  );
  onScopeDispose(clear);
}

export function usePageShellState() {
  const route = useRoute();
  const currentRegistration = computed(() => {
    const current = registeredPageShell.value;
    if (!current || !routeIncludesScope(route, current.scope)) return undefined;
    return current.key === routeIdentity(route, current.scope)
      ? current
      : undefined;
  });

  return {
    title: computed(() => currentRegistration.value?.title),
    breadcrumbLabel: computed(() => currentRegistration.value?.breadcrumbLabel),
    breadcrumbTos: computed(
      () => currentRegistration.value?.breadcrumbTos ?? {},
    ),
    extraBreadcrumbs: computed(
      () => currentRegistration.value?.extraBreadcrumbs ?? [],
    ),
  };
}
